import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { AsyncButton } from "./AsyncButton.js";
import { CostTag } from "./CostTag.js";
import { DeleteAccountSection } from "./DeleteAccountSection.js";
import { useT } from "../lib/i18n/store.js";
import { fallBackToSource, sizedImageUrl } from "../lib/imageUrl.js";
import { AutoTextarea } from "./AutoTextarea.js";
import { apiErrorMessage } from "../lib/apiErrorMessage.js";
import { isValidNewSupportLink, isValidSupportLink, isValidUsername } from "@lorevok/domain";
import { AUTHOR_SUPPORT_LINKS_ENABLED } from "../lib/features.js";

/** Matches the API's MAX_DISPLAY_NAME_LENGTH / MAX_BIO_LENGTH. */
const MAX_DISPLAY_NAME_LENGTH = 40;
const MAX_BIO_LENGTH = 280;
const MAX_AVATAR_PROMPT_LENGTH = 200;

function avatarInitial(name: string): string {
  const first = [...name.trim()][0];
  return first ? first.toUpperCase() : "?";
}

/**
 * Popup for editing the author's public profile — display name, bio, and avatar.
 * Name/bio are saved together on "Save". The avatar is generated by the image
 * provider from a short description (a PAID action), or removed; the preview
 * updates as soon as the new avatar is stored. Portaled to document.body like
 * the other modals.
 */
export function EditProfileModal({
  initialName,
  username = "",
  initialBio,
  initialSupportLink,
  avatarUrl,
  onSaveProfile,
  onGenerateAvatar,
  onRemoveAvatar,
  onClose,
  accountEmail = null,
  onDeleteAccount,
  avatarCostCredits = null
}: {
  initialName: string;
  /** The permanent username, shown read-only: it is the profile link. */
  username?: string;
  initialBio: string;
  /** The author's "Support the author" link ("" when unset). */
  initialSupportLink: string;
  avatarUrl: string | null;
  onSaveProfile: (name: string, bio: string, supportLink: string) => Promise<void>;
  /** Generate an avatar from a description (paid); resolves to the new URL. */
  onGenerateAvatar: (prompt: string) => Promise<string | null>;
  /** Clear the current avatar; resolves to the new URL (null). */
  onRemoveAvatar: () => Promise<string | null>;
  onClose: () => void;
  /** The account email (signed-in accounts only) — enables "Delete account". */
  accountEmail?: string | null;
  /** Permanently deletes the account; shown only with an accountEmail. */
  onDeleteAccount?: () => Promise<void>;
  /** What generating an avatar costs, worn on its button; null when unknown. */
  avatarCostCredits?: number | null;
}) {
  const t = useT();
  const [name, setName] = useState(initialName);
  const [bio, setBio] = useState(initialBio);
  const [supportLink, setSupportLink] = useState(initialSupportLink);
  // A new link must be https; one saved before that rule (http) may stay as it
  // is — the API accepts it back unchanged — until the author edits it.
  const supportLinkValid =
    supportLink.trim() === initialSupportLink.trim()
      ? isValidSupportLink(supportLink.trim())
      : isValidNewSupportLink(supportLink.trim());
  const [preview, setPreview] = useState<string | null>(avatarUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarPrompt, setAvatarPrompt] = useState("");
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pointerDownOnBackdrop = useRef(false);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    inputRef.current?.select();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  async function handleGenerateAvatar() {
    const prompt = avatarPrompt.trim();
    if (!prompt) return;
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      const url = await onGenerateAvatar(prompt);
      setPreview(url);
    } catch (err) {
      setAvatarError(apiErrorMessage(err, t, t("profile.avatarGenerateFailed")));
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleRemoveAvatar() {
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      await onRemoveAvatar();
      setPreview(null);
    } catch (err) {
      setAvatarError(apiErrorMessage(err, t, t("profile.avatarGenerateFailed")));
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("profile.nameRequired"));
      return;
    }
    if (!supportLinkValid) {
      setError(t("profile.supportLinkInvalid"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSaveProfile(trimmed, bio.trim(), supportLink.trim());
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t, t("profile.nameSaveFailed")));
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="admin-modal-backdrop"
      onMouseDown={(event) => {
        pointerDownOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pointerDownOnBackdrop.current) onClose();
      }}
    >
      <div
        tabIndex={-1}
        className="admin-modal edit-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="admin-modal-close" aria-label={t("popup.close")} onClick={onClose}>
          ×
        </button>
        <h2 id="edit-profile-title">{t("author.editProfile")}</h2>

        <div className="edit-profile-avatar-row">
          <div className="author-avatar edit-profile-avatar">
            {preview ? <img src={sizedImageUrl(preview, 128, { aspect: 1 })} onError={fallBackToSource(preview)} alt="" /> : <span aria-hidden="true">{avatarInitial(name)}</span>}
          </div>
          <div className="edit-profile-avatar-actions">
            <input
              className="edit-profile-avatar-prompt"
              type="text"
              value={avatarPrompt}
              maxLength={MAX_AVATAR_PROMPT_LENGTH}
              placeholder={t("profile.avatarPromptPlaceholder")}
              aria-label={t("profile.avatarPromptPlaceholder")}
              disabled={avatarBusy}
              onChange={(event) => setAvatarPrompt(event.target.value)}
            />
            <div className="edit-profile-avatar-buttons">
              <AsyncButton
                type="button"
                busy={avatarBusy}
                loadingLabel={t("profile.avatarGenerating")}
                disabled={avatarPrompt.trim().length === 0}
                onClick={() => void handleGenerateAvatar()}
              >
                {t("profile.generateAvatar")}
                {avatarCostCredits !== null && <CostTag credits={avatarCostCredits} />}
              </AsyncButton>
              {preview && (
                <button
                  type="button"
                  className="secondary"
                  disabled={avatarBusy}
                  onClick={() => void handleRemoveAvatar()}
                >
                  {t("profile.removeAvatar")}
                </button>
              )}
            </div>
            {avatarCostCredits === null && <p className="edit-profile-avatar-hint">{t("profile.avatarPaidHint")}</p>}
            {avatarError && <p className="invalid">{avatarError}</p>}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <label className="author-name-field">
            <span className="author-name-label">{t("profile.authorNameLabel")}</span>
            <input
              ref={inputRef}
              type="text"
              value={name}
              maxLength={MAX_DISPLAY_NAME_LENGTH}
              placeholder={t("profile.authorNamePlaceholder")}
              onChange={(event) => {
                setName(event.target.value);
                setError(null);
              }}
            />
            {isValidUsername(username) && (
              <span className="field-hint">{t("profile.usernamePermanent", { username })}</span>
            )}
          </label>
          <label className="author-name-field">
            <span className="author-name-label">{t("profile.bioLabel")}</span>
            <AutoTextarea
              rows={3}
              value={bio}
              maxLength={MAX_BIO_LENGTH}
              placeholder={t("profile.bioPlaceholder")}
              onChange={(event) => setBio(event.target.value)}
            />
          </label>
          {AUTHOR_SUPPORT_LINKS_ENABLED && (
          <div className="author-name-field">
            <label className="author-name-field">
              <span className="author-name-label">{t("profile.supportLinkLabel")}</span>
              <input
                type="url"
                inputMode="url"
                value={supportLink}
                placeholder="https://boosty.to/your-page"
                aria-invalid={!supportLinkValid || undefined}
                aria-describedby="edit-profile-support-hint"
                onChange={(event) => {
                  setSupportLink(event.target.value);
                  setError(null);
                }}
              />
            </label>
            <span id="edit-profile-support-hint" className={supportLinkValid ? "field-hint" : "invalid"}>
              {supportLinkValid ? t("profile.supportLinkHint") : t("profile.supportLinkInvalid")}
            </span>
          </div>
          )}
          {error && <p className="invalid">{error}</p>}
          <div className="button-row edit-profile-footer">
            <button type="button" className="secondary" disabled={busy} onClick={onClose}>
              {t("feedback.cancel")}
            </button>
            <AsyncButton type="submit" busy={busy} loadingLabel={t("profile.savingProfile")}>
              {t("profile.saveProfile")}
            </AsyncButton>
          </div>
        </form>
        {accountEmail && onDeleteAccount && <DeleteAccountSection email={accountEmail} onDelete={onDeleteAccount} />}
      </div>
    </div>,
    document.body
  );
}
